<div 
  class="page min-h-screen bg-white font-sans text-gray-900 selection:bg-orange-100 selection:text-brand flex flex-col relative overflow-hidden"
  data-testid="wizard-personalize-section"
>
  
  <!-- Background Decor -->
  <div class="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 pointer-events-none translate-x-1/3 -translate-y-1/3"></div>
  <div class="absolute bottom-0 left-0 w-[500px] h-[500px] bg-yellow-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 pointer-events-none -translate-x-1/3 translate-y-1/3"></div>

  <!-- Header / Logo -->
  <div class="w-full max-w-5xl mx-auto px-6 py-8 relative z-10">
    <a routerLink="/wizard/v2/landing" class="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="wizard-personalize-logo">
      <img src="images/logo.png" alt="JustTeach Logo" class="h-8 w-auto"/>
    </a>
  </div>

  <!-- Main Content -->
  <div class="flex-1 flex items-center justify-center px-4 pb-12 relative z-10">
    <div class="max-w-2xl w-full">

      <!-- Stepper -->
      <div class="mb-12" data-testid="wizard-personalize-stepper">
        <div class="flex items-start justify-center space-x-4">
          <!-- Step 1: Completed -->
          <div class="flex flex-col items-center gap-2" data-testid="wizard-personalize-stepper-step-0">
            <div class="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-sm shadow-md">
              <lucide-icon [img]="icons.Check" class="w-5 h-5"></lucide-icon>
            </div>
            <span class="font-bold text-sm text-green-600">Profile</span>
          </div>

          <!-- Connector Line -->
          <div class="w-16 h-0.5 bg-green-500 rounded-full mt-4"></div>

          <!-- Step 2: Completed -->
          <div class="flex flex-col items-center gap-2" data-testid="wizard-personalize-stepper-step-1">
            <div class="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-sm shadow-md">
              <lucide-icon [img]="icons.Check" class="w-5 h-5"></lucide-icon>
            </div>
            <span class="font-bold text-sm text-green-600">School</span>
          </div>

          <!-- Connector Line -->
          <div class="w-16 h-0.5 bg-brand rounded-full mt-4"></div>

          <!-- Step 3: Active -->
          <div class="flex flex-col items-center gap-2" data-testid="wizard-personalize-stepper-step-2">
            <div class="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center font-bold text-sm shadow-lg shadow-brand/20">
              3
            </div>
            <span class="font-bold text-sm text-brand">Personalize</span>
          </div>
        </div>
      </div>
      
      <div class="text-center mb-10">
        <h1 class="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4" data-testid="wizard-personalize-heading">
          Almost there
        </h1>
        <p class="text-gray-600 text-lg max-w-lg mx-auto leading-relaxed" data-testid="wizard-personalize-subheading">
          Help us understand your specific context so we can save you the most time.
        </p>
      </div>

      <div class="bg-white/80 backdrop-blur-xl border border-white shadow-2xl shadow-gray-200/50 rounded-2xl p-6 sm:p-10" data-testid="wizard-personalize-form-container">
        <form [formGroup]="personalizeForm" (ngSubmit)="onSubmit()" class="space-y-8" data-testid="wizard-personalize-form">

          <!-- Time Eaters (Checkboxes) -->
          <div class="space-y-3" data-testid="wizard-personalize-time-eaters-section">
            <label class="block text-sm font-bold text-gray-900" data-testid="wizard-personalize-time-eaters-label">
              What currently eats the most time in your week? <span class="text-brand">*</span>
            </label>
            <div class="space-y-2" data-testid="wizard-personalize-time-eaters-list">
              @for (option of timeOptions; track option; let i = $index) {
                <label 
                  class="checkbox-option flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 group"
                  [class.border-brand]="isTimeEaterSelected(option)"
                  [class.bg-orange-50]="isTimeEaterSelected(option)"
                  [class.border-gray-100]="!isTimeEaterSelected(option)"
                  [class.bg-white]="!isTimeEaterSelected(option)"
                  [class.hover:border-orange-200]="!isTimeEaterSelected(option)"
                  [class.hover:bg-gray-50]="!isTimeEaterSelected(option)"
                  [attr.data-testid]="'wizard-personalize-time-eater-' + i"
                >
                  <div 
                    class="checkbox-box relative flex items-center justify-center w-6 h-6 rounded-md border-2 transition-all duration-200"
                    [class.bg-brand]="isTimeEaterSelected(option)"
                    [class.border-brand]="isTimeEaterSelected(option)"
                    [class.border-gray-300]="!isTimeEaterSelected(option)"
                    [class.bg-white]="!isTimeEaterSelected(option)"
                    [class.group-hover:border-gray-400]="!isTimeEaterSelected(option)"
                  >
                    @if (isTimeEaterSelected(option)) {
                      <lucide-icon [img]="icons.Check" class="w-4 h-4 text-white"></lucide-icon>
                    }
                    <input
                      type="checkbox"
                      [checked]="isTimeEaterSelected(option)"
                      (change)="toggleTimeEater(option)"
                      class="hidden"
                    />
                  </div>
                  <span 
                    class="font-medium"
                    [class.text-brand]="isTimeEaterSelected(option)"
                    [class.text-gray-700]="!isTimeEaterSelected(option)"
                  >
                    {{ option }}
                  </span>
                </label>
              }
            </div>
            @if (selectedTimeEaters().length === 0 && personalizeForm.touched) {
              <p class="text-sm text-red-500 font-medium" data-testid="wizard-personalize-time-eaters-error">Please select at least one option</p>
            }
          </div>

          <!-- AI Help (Textarea) -->
          <div class="space-y-3" data-testid="wizard-personalize-ai-help-section">
            <label class="block text-sm font-bold text-gray-900" data-testid="wizard-personalize-ai-help-label">
              What would you want an AI assistant like this to help you with first? <span class="text-brand">*</span>
            </label>
            <textarea
              formControlName="aiHelp"
              rows="3"
              class="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium resize-none"
              [class.border-red-400]="personalizeForm.get('aiHelp')?.invalid && personalizeForm.get('aiHelp')?.touched"
              placeholder="Tell us what you need help with..."
              data-testid="wizard-personalize-ai-help-input"
            ></textarea>
            @if (personalizeForm.get('aiHelp')?.invalid && personalizeForm.get('aiHelp')?.touched) {
              <p class="text-sm text-red-500 font-medium" data-testid="wizard-personalize-ai-help-error">Please tell us what you need help with</p>
            }
          </div>

          <!-- Use Platform (Radio + Textarea) -->
          <div class="space-y-3" data-testid="wizard-personalize-use-platform-section">
            <label class="block text-sm font-bold text-gray-900" data-testid="wizard-personalize-use-platform-label">
              Would you use this platform at your school? <span class="text-brand">*</span>
            </label>
            <div class="flex flex-col gap-3">
              <div class="flex flex-wrap gap-4 mb-2" data-testid="wizard-personalize-use-platform-options">
                @for (option of yesNoMaybeOptions; track option; let i = $index) {
                  <label 
                    class="radio-pill flex items-center gap-2 px-5 py-2.5 rounded-full border-2 cursor-pointer transition-all duration-200"
                    [class.border-brand]="personalizeForm.get('usePlatform')?.value === option"
                    [class.bg-brand]="personalizeForm.get('usePlatform')?.value === option"
                    [class.text-white]="personalizeForm.get('usePlatform')?.value === option"
                    [class.shadow-md]="personalizeForm.get('usePlatform')?.value === option"
                    [class.shadow-brand/20]="personalizeForm.get('usePlatform')?.value === option"
                    [class.border-gray-200]="personalizeForm.get('usePlatform')?.value !== option"
                    [class.bg-white]="personalizeForm.get('usePlatform')?.value !== option"
                    [class.text-gray-600]="personalizeForm.get('usePlatform')?.value !== option"
                    [class.hover:border-brand]="personalizeForm.get('usePlatform')?.value !== option"
                    [class.hover:text-brand]="personalizeForm.get('usePlatform')?.value !== option"
                    [attr.data-testid]="'wizard-personalize-use-platform-option-' + i"
                  >
                    <div 
                      class="w-4 h-4 rounded-full border-2 flex items-center justify-center"
                      [class.border-white]="personalizeForm.get('usePlatform')?.value === option"
                      [class.border-gray-300]="personalizeForm.get('usePlatform')?.value !== option"
                    >
                      @if (personalizeForm.get('usePlatform')?.value === option) {
                        <div class="w-2 h-2 rounded-full bg-white"></div>
                      }
                    </div>
                    <span class="font-bold text-sm">{{ option }}</span>
                    <input
                      type="radio"
                      [value]="option"
                      [checked]="personalizeForm.get('usePlatform')?.value === option"
                      (change)="setUsePlatform(option)"
                      class="hidden"
                    />
                  </label>
                }
              </div>
              @if (personalizeForm.get('usePlatform')?.invalid && personalizeForm.get('usePlatform')?.touched) {
                <p class="text-sm text-red-500 font-medium" data-testid="wizard-personalize-use-platform-error">Please select an option</p>
              }
              <textarea
                formControlName="usePlatformReason"
                rows="2"
                class="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium resize-none"
                placeholder="Why (or why not)?"
                data-testid="wizard-personalize-use-platform-reason-input"
              ></textarea>
            </div>
          </div>

          <div class="pt-6">
            <button 
              type="submit"
              class="w-full bg-brand hover:bg-brand-dark text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-brand/20 flex items-center justify-center gap-2 group text-lg disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
              [disabled]="isLoading$ | async"
              data-testid="wizard-personalize-submit-btn"
            >
              @if (isLoading$ | async) {
                <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              } @else {
                Send Answers
              }
            </button>
          </div>

        </form>
      </div>
    </div>
  </div>
</div>
